/**
 * PrimaNotaTab — Prima Nota / Registro Incassi (Step 2, 28/02/2026)
 *
 * Vista unificata cassa giornaliera:
 *  • payments[] di fatture/proforma esistenti (read-only, "virtual")
 *  • cash_movements manuali (incassi liberi + uscite)
 *
 * Implementazione FREEZE-safe seguendo il pattern Lavoro Quotidiano
 * Rebuilt (no Radix Select, no portal, no AnimatePresence). Tutti i
 * controlli sono nativi HTML + state React locale.
 */

import React, { useEffect, useMemo, useState, useCallback } from "react";
import api from "@/api";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import ClientCombobox from "@/components/ClientCombobox";
import FinancialVisibilityGuard from "@/components/permissions/FinancialVisibilityGuard";
import { canViewFinancialDashboard } from "@/lib/permissions";

const todayISO = () => new Date().toISOString().slice(0, 10);
// P3.c (04/06/2026) — formatter ISO → gg/mm/aaaa per visualizzazione UI.
// I campi `<input type="date">` continuano a usare ISO (richiesto dal
// browser); il DB resta invariato. Questa funzione è SOLO per render.
const fmtDateIT = (iso) => {
  if (!iso) return "";
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
};
// P3 (04/06/2026) — primo giorno del mese corrente, formato YYYY-MM-DD.
// Usato come default `fromDate` all'apertura del tab e nel Reset filtri.
const firstOfMonthISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};
const nowHM = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};
const eur = (n) => `${(Number(n) || 0).toFixed(2).replace(".", ",")} €`;

const METHODS = ["Contanti", "POS", "Bonifico", "Assegno", "Altro"];
const OUT_CATEGORIES = [
  "Marca da bollo", "Diritti", "Visure", "F24",
  "Spedizioni", "Anticipazioni cliente", "Cancelleria", "Altro",
];

const inputStyle = { padding: "6px 8px", border: "1px solid #cbd5e1", borderRadius: 4, fontSize: 13, width: "100%", boxSizing: "border-box" };
const labelStyle = { display: "block", fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 3, textTransform: "uppercase", letterSpacing: 0.3 };

function KpiCard({ label, value, color, testid, hint }) {
  return (
    <div style={{ flex: 1, background: "white", border: "1px solid #e2e8f0", borderRadius: 6, padding: "10px 14px", minWidth: 140 }} data-testid={testid} title={hint || undefined}>
      <div style={{ fontSize: 11, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color, marginTop: 4 }}>{value}</div>
    </div>
  );
}

function InlineModal({ open, onClose, title, children, footer }) {
  if (!open) return null;
  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16 }}
      data-testid="pn-modal-backdrop"
    >
      <div
        style={{ background: "white", borderRadius: 8, boxShadow: "0 10px 25px rgba(0,0,0,0.15)", width: "100%", maxWidth: 560, maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: "12px 16px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{title}</h3>
          <button type="button" onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#64748b" }} aria-label="Chiudi" data-testid="pn-modal-close">×</button>
        </div>
        <div style={{ padding: 16, overflowY: "auto", flex: 1 }}>{children}</div>
        {footer && (
          <div style={{ padding: "10px 16px", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "flex-end", gap: 8 }}>{footer}</div>
        )}
      </div>
    </div>
  );
}

export default function PrimaNotaTab({
  clients = [],
  cases = [],
  operators = [],
  currentOperator,
  // P2 (04/06/2026): ClientCombobox inline-creation plumbing — identical
  // to the pattern used by Diario / Lavoro Quotidiano.
  onOpenNewClient,
  isClientDialogOpen,
  setIsClientDialogOpen,
  setClientForm,
  resetClientForm,
  // 07/07/2026 — visibilità riepiloghi finanziari (P0 security refinement)
  operatorRole = "amministratore",
}) {
  // 07/07/2026 — Financial visibility policy (P0): la colonna "Saldo prog."
  // e le sue voci negli export non devono essere accessibili agli operator.
  // Alias derivato dal permesso centralizzato per leggibilità nei branch UI.
  const canSeeRunningBalance = canViewFinancialDashboard(operatorRole);

  const [movements, setMovements] = useState([]);
  const [summary, setSummary] = useState({ totaleEntrate: 0, totaleUscite: 0, saldo: 0, movimenti: 0, art15: 0, perMetodo: {} });
  const [loading, setLoading] = useState(false);

  // Filters
  const [fromDate, setFromDate] = useState(firstOfMonthISO());
  const [toDate, setToDate] = useState(todayISO());
  const [filterDirection, setFilterDirection] = useState("all");
  const [filterClientId, setFilterClientId] = useState("");
  const [filterCaseId, setFilterCaseId] = useState("");
  // 11/06/2026 — filterMethod (single string) sostituito da filterMethods (array, multi-select).
  const [filterOperatorId, setFilterOperatorId] = useState("");
  const [filterDocType, setFilterDocType] = useState("");
  // 11/06/2026 — Filtro Studio/Agenzia (su richiesta utente). Default
  // "agenzia": all'apertura della Prima Nota vediamo i movimenti
  // dell'area Agenzia; l'utente può commutare a "studio" o "all".
  const [filterManagementType, setFilterManagementType] = useState("agenzia");
  // 11/06/2026 — Filtro metodi pagamento MULTI-SELECT (richiesta utente).
  // Array di metodi selezionati; vuoto = "Tutti". Sostituisce il vecchio
  // `filterMethod: string`. Le legacy "Cassa" sono trattate come
  // "Contanti" dal backend, quindi qui usiamo SOLO i 5 valori canonici.
  const [filterMethods, setFilterMethods] = useState([]);
  const [methodsMenuOpen, setMethodsMenuOpen] = useState(false);
  // P3 (04/06/2026) — ricerca testo libero (client-side, su tutti i
  // campi rilevanti). Funziona insieme agli altri filtri.
  const [searchText, setSearchText] = useState("");

  // Dialog state
  const [showInDialog, setShowInDialog] = useState(false);
  const [showOutDialog, setShowOutDialog] = useState(false);
  // 11/06/2026 — Correzione movimento manuale (richiesta utente).
  // Movimenti con `readonly=true` (legati a fatture/proforma) NON
  // sono correggibili dalla Prima Nota: per quelli si rimanda al
  // documento. Per gli altri movimenti, click su "Correzione" apre il
  // MovementDialog precompilato in modalità edit (PUT).
  const [editingMovement, setEditingMovement] = useState(null);
  const openCorrection = useCallback((m) => {
    if (m.readonly) {
      toast.info(
        m.invoiceNumber
          ? `Correggi direttamente il documento ${m.invoiceNumber} (${m.invoiceDocType || "fattura"})`
          : "Movimento documentale — la correzione deve essere fatta sul documento collegato"
      );
      return;
    }
    setEditingMovement(m);
  }, []);

  // 11/06/2026 — Filtri avanzati collassabili (su richiesta utente).
  // I filtri "essenziali" (ricerca, Anno, Mese, Tipo, Metodo) restano
  // sempre visibili; quelli "avanzati" (Da/A, Cliente, Pratica,
  // Operatore, Documento) sono nascosti di default per liberare spazio
  // verticale e mostrare subito le prime righe della tabella.
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const advancedFiltersActiveCount = useMemo(() => {
    let n = 0;
    if (filterClientId) n++;
    if (filterCaseId) n++;
    if (filterOperatorId) n++;
    if (filterDocType) n++;
    // Conta Da/A come "avanzato attivo" solo se l'utente li ha modificati
    // rispetto a quanto auto-impostato da Anno/Mese (vedi
    // applyMonthYearQuickFilter). Calcoliamo lo stato di default atteso
    // per il mese/anno correnti selezionati e confrontiamo.
    const _yearFrom = (fromDate || "").slice(0, 4);
    const _yearTo = (toDate || "").slice(0, 4);
    const _isWholeYear =
      _yearFrom &&
      _yearFrom === _yearTo &&
      (fromDate || "").endsWith("-01-01") &&
      (toDate || "").endsWith("-12-31");
    const _isMonthRange =
      _yearFrom === _yearTo &&
      (fromDate || "").slice(5, 7) === (toDate || "").slice(5, 7) &&
      (fromDate || "").endsWith("-01");
    if (!_isWholeYear && !_isMonthRange) n++;
    return n;
  }, [filterClientId, filterCaseId, filterOperatorId, filterDocType, fromDate, toDate]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.set("from", fromDate);
      if (toDate) params.set("to", toDate);
      if (filterDirection && filterDirection !== "all") params.set("direction", filterDirection);
      if (filterClientId) params.set("clientId", filterClientId);
      if (filterCaseId) params.set("caseId", filterCaseId);
      if (filterMethods.length > 0)
        params.set("paymentMethod", filterMethods.join(","));
      if (filterOperatorId) params.set("operatorId", filterOperatorId);
      if (filterDocType) params.set("docType", filterDocType);
      if (filterManagementType && filterManagementType !== "all")
        params.set("managementType", filterManagementType);
      const [list, sum] = await Promise.all([
        api.request(`/api/cash-movements?${params.toString()}`),
        api.request(`/api/cash-movements/summary?from=${fromDate}&to=${toDate}${
          filterManagementType && filterManagementType !== "all"
            ? `&managementType=${filterManagementType}`
            : ""
        }${
          filterMethods.length > 0 ? `&paymentMethod=${encodeURIComponent(filterMethods.join(","))}` : ""
        }`),
      ]);
      setMovements(Array.isArray(list) ? list : []);
      setSummary(sum || {});
    } catch (err) {
      console.error("PrimaNota refresh failed:", err);
      toast.error(err?.message || "Errore caricamento Prima Nota");
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, filterDirection, filterClientId, filterCaseId, filterMethods, filterOperatorId, filterDocType, filterManagementType]);

  useEffect(() => { refresh(); }, [refresh]);

  // Saldo progressivo (computed): ordina ASC, accumula, e RESTITUISCE
  // l'array in ordine DESC (più recente in alto). Decisione 11/06/2026 —
  // su richiesta utente: aprendo il Registro Incassi voglio vedere
  // SUBITO l'ultimo movimento registrato, senza scorrere. Il saldo
  // progressivo resta calcolato contabilmente in ordine cronologico
  // (ASC) e poi l'array viene invertito: ogni riga mostra il saldo
  // risultante DOPO quel movimento (coerente con la lettura dall'alto:
  // riga 1 = ultimo movimento + saldo finale; riga N = primo movimento
  // + saldo iniziale post-prima-registrazione).
  const rowsWithBalance = useMemo(() => {
    const asc = [...movements].sort((a, b) =>
      ((a.date || "") + (a.time || "") + (a.createdAt || "")).localeCompare(
        (b.date || "") + (b.time || "") + (b.createdAt || "")
      )
    );
    let running = 0;
    const withBal = asc.map((m) => {
      const amt = Number(m.amount) || 0;
      running += m.direction === "in" ? amt : -amt;
      return { ...m, _runningBalance: running };
    });
    // Reverse: DESC per la visualizzazione (più recente in alto).
    return withBal.reverse();
  }, [movements]);

  // P3 (04/06/2026) — filtro ricerca testo libero (client-side).
  // Lavora INSIEME agli altri filtri server-side: agiamo sull'output di
  // `rowsWithBalance` così la barra di ricerca filtra solo ciò che è
  // già nel periodo/cliente/metodo selezionati. Il saldo progressivo
  // resta coerente perché calcolato PRIMA del filtro testo (i totali
  // si riferiscono al periodo, non alla stringa cercata).
  const visibleRows = useMemo(() => {
    const q = (searchText || "").trim().toLowerCase();
    if (!q) return rowsWithBalance;
    return rowsWithBalance.filter((m) => {
      const docTypeLabel = m.invoiceDocType === "fattura" ? "fattura"
        : m.invoiceDocType === "proforma" ? "proforma" : "";
      const haystack = [
        m.clientName,
        m.caseNumber,
        m.invoiceNumber,
        docTypeLabel,
        m.description,
        m.notes,
        m.category,
        m.paymentMethod,
        m.operatorName,
      ].map((v) => (v || "").toString().toLowerCase()).join(" \n ");
      return haystack.includes(q);
    });
  }, [rowsWithBalance, searchText]);

  // 07/06/2026 — KPI "Compensi Professionali" = Entrate - Anticipazioni
  // Art. 15. Calcolato da `visibleRows` per restare coerente con TUTTI i
  // filtri attivi (periodo + cliente + pratica + metodo + operatore +
  // documento + ricerca libera). Solo movimenti `in` partecipano alle
  // Entrate; Art. 15 viene sottratto sia dai movimenti `in` documentali
  // (incassi da fattura/proforma con `speseArt15`) sia da quelli `in`
  // manuali (incassi liberi con `art15Amount` valorizzato). Per
  // simmetria fiscale, Art. 15 si applica solo ai movimenti `in`.
  const compensiProfessionali = useMemo(() => {
    let entrate = 0;
    let art15 = 0;
    for (const m of visibleRows) {
      if (m.direction === "in") {
        entrate += Number(m.amount) || 0;
        art15 += Number(m.art15Amount) || 0;
      }
    }
    return entrate - art15;
  }, [visibleRows]);

  const cancelMovement = async (m) => {
    if (m.readonly) {
      toast.info("Movimento documentale (fattura/proforma). Annullarlo dal documento.");
      return;
    }
    const reason = window.prompt("Motivo dell'annullamento (obbligatorio):");
    if (!reason || !reason.trim()) return;
    try {
      await api.request(`/api/cash-movements/${m.id}?reason=${encodeURIComponent(reason.trim())}`, { method: "DELETE" });
      toast.success("Movimento annullato");
      refresh();
    } catch (err) {
      toast.error(err?.message || "Errore annullamento");
    }
  };

  const resetFilters = () => {
    setFromDate(firstOfMonthISO()); setToDate(todayISO());
    setFilterDirection("all"); setFilterClientId(""); setFilterCaseId("");
    setFilterMethods([]); setFilterOperatorId(""); setFilterDocType("");
    setFilterManagementType("agenzia");
    setSearchText("");
  };

  // P3.b (04/06/2026) — scorciatoia per impostare Da/A su un intero mese.
  // Se il mese selezionato è quello corrente, la data A viene fissata
  // a oggi anziché all'ultimo giorno del mese (così non vediamo "future
  // empty rows" nei mesi in corso).
  // 07/06/2026 — Esteso per supportare `month = 0` ("Tutti") che imposta
  // l'intero anno: Da = 01/01/YYYY, A = 31/12/YYYY (senza cap su oggi
  // anche se l'anno è quello corrente — consente verifiche annuali
  // complete da subito).
  const applyMonthYearQuickFilter = useCallback((year, month) => {
    if (!month || month === 0) {
      const fromISO = `${year}-01-01`;
      const toISO = `${year}-12-31`;
      setFromDate(fromISO);
      setToDate(toISO);
      return;
    }
    const today = new Date();
    const isCurrentMonth =
      year === today.getFullYear() && month === today.getMonth() + 1;
    const fromISO = `${year}-${String(month).padStart(2, "0")}-01`;
    let toISO;
    if (isCurrentMonth) {
      toISO = todayISO();
    } else {
      // ultimo giorno del mese: new Date(year, month, 0).getDate()
      const lastDay = new Date(year, month, 0).getDate();
      toISO = `${year}-${String(month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
    }
    setFromDate(fromISO);
    setToDate(toISO);
  }, []);

  // ─── P4 (04/06/2026) — Export Excel / PDF ────────────────────────────
  // Helper: trasforma una riga `visibleRows` nella rappresentazione
  // user-facing (date IT, importi, doc-type esteso).
  const buildExportRows = useCallback(() => {
    return visibleRows.map((m) => {
      const isIn = m.direction === "in";
      const docLabel = m.invoiceNumber
        ? `${m.invoiceNumber} (${m.invoiceDocType || "?"})`
        : "";
      const row = {
        Data: fmtDateIT(m.date),
        Tipo: isIn ? "Entrata" : "Uscita",
        Cliente: m.clientName || "",
        Pratica: m.caseNumber || "",
        Documento: docLabel,
        Operatore: m.operatorName || "",
        Descrizione: m.description || "",
        Metodo: m.paymentMethod || "",
        Entrata: isIn ? Number(m.amount) || 0 : 0,
        Uscita: !isIn ? Number(m.amount) || 0 : 0,
        "Art. 15": Number(m.art15Amount) || 0,
      };
      // Saldo progressivo: escluso per operator ("Financial Visibility P0"
      // — 07/07/2026). Il campo non deve figurare né nel PDF né nel XLS.
      if (canSeeRunningBalance) {
        row["Saldo prog."] = Number(m._runningBalance) || 0;
      }
      return row;
    });
  }, [visibleRows, canSeeRunningBalance]);

  const handleExportExcel = useCallback(() => {
    try {
      const rows = buildExportRows();
      const wb = XLSX.utils.book_new();
      // 07/07/2026 P0 extension: aggregati manageriali nascosti agli
      // operator anche negli export. Righe operative (dati per movimento)
      // sempre presenti — non è row-level.
      const showAggregates = canViewFinancialDashboard(operatorRole);
      // Header rows (intestazione + riepilogo)
      const periodLabel = `${fmtDateIT(fromDate)} → ${fmtDateIT(toDate)}`;
      const now = new Date();
      const ts = now.toLocaleString("it-IT");
      // Compensi Professionali per l'export = Entrate - Art.15 sulle
      // RIGHE esportate (rows = visibleRows già passate da buildExportRows).
      const _compEntrate = rows.reduce((s, r) => s + (Number(r.Entrata) || 0), 0);
      const _compArt15 = rows.reduce((s, r) => s + (Number(r["Art. 15"]) || 0), 0);
      const _compensi = _compEntrate - _compArt15;
      const headerAoa = [
        ["Prima Nota / Registro Incassi"],
        [`Periodo: ${periodLabel}`],
        // 11/06/2026 — Filtro Studio/Agenzia esposto nell'header di
        // export per coerenza con quanto visualizzato a schermo.
        [`Tipo attività: ${
          filterManagementType === "studio" ? "Studio"
          : filterManagementType === "agenzia" ? "Agenzia"
          : "Tutti (Studio + Agenzia)"
        }`],
        [`Metodi: ${filterMethods.length === 0 ? "Tutti" : filterMethods.join(", ")}`],
        [`Generato il: ${ts}`],
        [],
        // Blocco RIEPILOGO — solo per admin/responsabile
        ...(showAggregates ? [
          ["RIEPILOGO"],
          ["Totale entrate", summary.totaleEntrate || 0],
          ["Totale uscite", summary.totaleUscite || 0],
          ["Saldo periodo", summary.saldo || 0],
          ["Numero movimenti", rows.length],
          ["Art. 15 del periodo", summary.art15 || 0],
          ["Compensi professionali (Entrate - Art. 15)", _compensi],
          [],
        ] : []),
        // colonne tabella
      ];
      const ws = XLSX.utils.aoa_to_sheet(headerAoa);
      // Append tabella in sequenza — l'header dell'Excel esclude
      // "Saldo prog." per operator (Financial Visibility P0).
      const excelHeader = [
        "Data", "Tipo", "Cliente", "Pratica", "Documento", "Operatore",
        "Descrizione", "Metodo", "Entrata", "Uscita", "Art. 15",
      ];
      if (canSeeRunningBalance) excelHeader.push("Saldo prog.");
      XLSX.utils.sheet_add_json(ws, rows, { origin: -1, header: excelHeader });
      // Larghezze colonne (in caratteri) — l'ultima colonna extra è
      // presente solo per admin/responsabile.
      const colWidths = [
        { wch: 12 }, { wch: 8 }, { wch: 26 }, { wch: 10 }, { wch: 18 },
        { wch: 18 }, { wch: 36 }, { wch: 12 }, { wch: 11 }, { wch: 11 }, { wch: 11 },
      ];
      if (canSeeRunningBalance) colWidths.push({ wch: 13 });
      ws["!cols"] = colWidths;
      XLSX.utils.book_append_sheet(wb, ws, "Prima Nota");
      const fname = `PrimaNota_${fromDate}_${toDate}.xlsx`;
      XLSX.writeFile(wb, fname);
      toast.success(`Excel esportato: ${fname}`);
    } catch (e) {
      console.error(e);
      toast.error("Errore durante l'esportazione Excel");
    }
  }, [buildExportRows, fromDate, toDate, summary, filterManagementType, filterMethods, operatorRole, canSeeRunningBalance]);

  const handleExportPdf = useCallback(() => {
    try {
      const rows = buildExportRows();
      // 07/07/2026 P0 extension: gli aggregati in header (KPI Entrate/
      // Uscite/Saldo/Art.15/Compensi) non compaiono nel PDF degli operator.
      const showAggregates = canViewFinancialDashboard(operatorRole);
      const periodLabel = `${fmtDateIT(fromDate)} → ${fmtDateIT(toDate)}`;
      const now = new Date();
      const ts = now.toLocaleString("it-IT");
      const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({
        "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;",
      }[c]));
      const fmtEur = (n) => (Number(n) || 0).toLocaleString("it-IT", {
        style: "currency", currency: "EUR",
      });
      // Compensi Professionali sul PDF = Entrate - Art.15 sulle righe
      // realmente esportate (coerente con i filtri attivi).
      const _pdfEntrate = rows.reduce((s, r) => s + (Number(r.Entrata) || 0), 0);
      const _pdfArt15 = rows.reduce((s, r) => s + (Number(r["Art. 15"]) || 0), 0);
      const _pdfCompensi = _pdfEntrate - _pdfArt15;
      const trs = rows.map((r) => `
        <tr>
          <td>${esc(r.Data)}</td>
          <td>${esc(r.Tipo)}</td>
          <td>${esc(r.Cliente)}</td>
          <td>${esc(r.Pratica)}</td>
          <td>${esc(r.Documento)}</td>
          <td>${esc(r.Operatore)}</td>
          <td>${esc(r.Descrizione)}</td>
          <td>${esc(r.Metodo)}</td>
          <td class="r in">${r.Entrata ? fmtEur(r.Entrata) : ""}</td>
          <td class="r out">${r.Uscita ? fmtEur(r.Uscita) : ""}</td>
          <td class="r art15">${r["Art. 15"] ? fmtEur(r["Art. 15"]) : ""}</td>
          ${canSeeRunningBalance ? `<td class="r">${fmtEur(r["Saldo prog."])}</td>` : ""}
        </tr>`).join("");
      const html = `<!doctype html><html lang="it"><head><meta charset="utf-8">
<title>Prima Nota ${periodLabel}</title>
<style>
  @page { size: A4 landscape; margin: 14mm; }
  body { font-family: -apple-system, "Segoe UI", Roboto, Arial, sans-serif; color:#0f172a; font-size:10px; }
  h1 { margin:0 0 4px; font-size:16px; }
  .meta { color:#64748b; font-size:10px; margin-bottom:10px; }
  .kpi { display:flex; gap:16px; padding:8px 10px; border:1px solid #e2e8f0; border-radius:4px; margin-bottom:10px; background:#f8fafc; font-size:10px; }
  .kpi b { color:#0f172a; }
  table { width:100%; border-collapse:collapse; font-size:9px; }
  thead { display:table-header-group; }
  th, td { border:1px solid #e2e8f0; padding:4px 6px; text-align:left; vertical-align:top; }
  th { background:#f1f5f9; font-weight:600; }
  td.r { text-align:right; white-space:nowrap; }
  td.in { color:#047857; font-weight:600; }
  td.out { color:#dc2626; font-weight:600; }
  td.art15 { color:#7c3aed; font-weight:600; }
  .kpi .compensi b { color:#0891b2; }
  .footer { position: fixed; bottom: 4mm; left: 0; right: 0; text-align: center; font-size:9px; color:#94a3b8; }
  @media print { .no-print { display:none; } }
</style></head><body>
<h1>Prima Nota / Registro Incassi</h1>
<div class="meta">Periodo: <b>${periodLabel}</b> · Tipo attività: <b>${
  filterManagementType === "studio" ? "Studio"
  : filterManagementType === "agenzia" ? "Agenzia"
  : "Tutti"
}</b> · Metodi: <b>${esc(filterMethods.length === 0 ? "Tutti" : filterMethods.join(", "))}</b> · Generato il ${esc(ts)}</div>
${showAggregates ? `
<div class="kpi">
  <span>Entrate: <b>${fmtEur(summary.totaleEntrate || 0)}</b></span>
  <span>Uscite: <b>${fmtEur(summary.totaleUscite || 0)}</b></span>
  <span>Saldo: <b>${fmtEur(summary.saldo || 0)}</b></span>
  <span>Movimenti: <b>${rows.length}</b></span>
  <span>Art. 15: <b>${fmtEur(summary.art15 || 0)}</b></span>
  <span class="compensi">Compensi Professionali: <b>${fmtEur(_pdfCompensi)}</b></span>
</div>` : ''}
<table>
  <thead><tr>
    <th>Data</th><th>Tipo</th><th>Cliente</th><th>Pratica</th><th>Documento</th>
    <th>Operatore</th><th>Descrizione</th><th>Metodo</th>
    <th class="r">Entrata</th><th class="r">Uscita</th><th class="r">Art. 15</th>${canSeeRunningBalance ? '<th class="r">Saldo prog.</th>' : ''}
  </tr></thead>
  <tbody>${trs || `<tr><td colspan="${canSeeRunningBalance ? 12 : 11}" style="text-align:center;color:#94a3b8;padding:14px">Nessun movimento nel periodo filtrato.</td></tr>`}</tbody>
</table>
<div class="footer">Prima Nota — pagina <span class="pageNum"></span></div>
<script>setTimeout(function(){ try { window.focus(); window.print(); } catch(e){} }, 200);</script>
</body></html>`;
      const w = window.open("", "_blank");
      if (!w) {
        toast.error("Popup bloccato. Abilita i popup per stampare la Prima Nota.");
        return;
      }
      w.document.open();
      w.document.write(html);
      w.document.close();
    } catch (e) {
      console.error(e);
      toast.error("Errore durante la generazione PDF");
    }
  }, [buildExportRows, fromDate, toDate, summary, filterManagementType, filterMethods, operatorRole, canSeeRunningBalance]);

  return (
    <div data-testid="prima-nota-tab" style={{ padding: 4 }}>
      {/* HEADER */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#0f172a" }}>
            Prima Nota / Registro Incassi
          </h2>
          <p style={{ margin: "2px 0 0", fontSize: 12, color: "#64748b" }}>
            Cassa giornaliera dello studio — entrate documentali + manuali + uscite.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={handleExportPdf}
            style={{ padding: "8px 14px", background: "white", color: "#475569", border: "1px solid #cbd5e1", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600 }}
            data-testid="pn-export-pdf-btn"
            title="Stampa o salva PDF della tabella filtrata"
          >🖨 Stampa PDF</button>
          <button
            type="button"
            onClick={handleExportExcel}
            style={{ padding: "8px 14px", background: "white", color: "#047857", border: "1px solid #10b981", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600 }}
            data-testid="pn-export-xlsx-btn"
            title="Esporta in Excel (.xlsx) la tabella filtrata"
          >📊 Esporta Excel</button>
          <button
            type="button"
            onClick={() => setShowInDialog(true)}
            style={{ padding: "8px 14px", background: "#10b981", color: "white", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600 }}
            data-testid="pn-register-in-btn"
          >+ Registra incasso</button>
          <button
            type="button"
            onClick={() => setShowOutDialog(true)}
            style={{ padding: "8px 14px", background: "#ef4444", color: "white", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600 }}
            data-testid="pn-register-out-btn"
          >+ Registra uscita</button>
        </div>
      </div>

      {/* KPI ROW — visibili solo ai ruoli con `canViewFinancialDashboard`
          (07/07/2026 P0 security). Nascosti sia gli aggregati numerici
          sia il breakdown per metodo di pagamento. Il layout si
          compatta naturalmente. */}
      <FinancialVisibilityGuard operatorRole={operatorRole}>
        <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" }} data-testid="pn-financial-summary">
          <KpiCard label="Totale entrate" value={eur(summary.totaleEntrate)} color="#059669" testid="pn-kpi-in" />
          <KpiCard label="Totale uscite" value={eur(summary.totaleUscite)} color="#dc2626" testid="pn-kpi-out" />
          <KpiCard label="Saldo periodo" value={eur(summary.saldo)} color={summary.saldo >= 0 ? "#0f172a" : "#dc2626"} testid="pn-kpi-balance" />
          <KpiCard label="Movimenti" value={String(summary.movimenti || 0)} color="#475569" testid="pn-kpi-count" />
          <KpiCard
            label="Tot. Anticipazioni Art. 15"
            value={eur(summary.art15 || 0)}
            color="#7c3aed"
            testid="pn-kpi-art15"
            hint="Somma Art. 15 di Fatture + Proforma + Incassi liberi del periodo. Anticipazioni in nome e per conto del cliente (diritti, bolli, visure, contributi)."
          />
          <KpiCard
            label="Compensi Professionali"
            value={eur(compensiProfessionali)}
            color="#0891b2"
            testid="pn-kpi-compensi"
            hint="Entrate al netto delle Anticipazioni Art. 15. Rappresenta la quota di incassi effettivamente riferibile all'attività professionale (compensi), escludendo i rimborsi per diritti, bolli, visure e contributi anticipati per conto cliente. Aggiornato in base ai filtri attivi (periodo, cliente, pratica, operatore, ricerca libera)."
          />
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }} data-testid="pn-method-chips">
          {METHODS.map((m) => (
            <div key={m} style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 16, padding: "4px 12px", fontSize: 12, color: "#475569" }} data-testid={`pn-method-chip-${m}`}>
              {m}: <strong style={{ color: (summary.perMetodo?.[m] ?? 0) >= 0 ? "#059669" : "#dc2626" }}>{eur(summary.perMetodo?.[m] ?? 0)}</strong>
            </div>
          ))}
        </div>
      </FinancialVisibilityGuard>

      {/* FILTERS — riorganizzati 11/06/2026 (su richiesta utente):
          - Sempre visibili: ricerca, Anno, Mese, Tipo, Metodo
          - Collassabili in "Filtri avanzati": Da/A, Cliente, Pratica,
            Operatore, Documento
          Obiettivo: meno spazio verticale → tabella visibile subito. */}
      <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 6, padding: 10, marginBottom: 12 }}>
        {/* Barra ricerca testo libero full-width (filtro più usato) */}
        <div style={{ position: "relative", marginBottom: 8 }}>
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Cerca cliente, pratica, documento, descrizione..."
            style={{
              width: "100%", boxSizing: "border-box",
              padding: "8px 36px 8px 32px",
              border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 13,
              background: "white",
            }}
            data-testid="pn-filter-search"
            aria-label="Ricerca testo libero"
          />
          <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: 14, pointerEvents: "none" }}>🔍</span>
          {searchText && (
            <button
              type="button"
              onClick={() => setSearchText("")}
              data-testid="pn-filter-search-clear"
              aria-label="Cancella ricerca"
              title="Cancella ricerca"
              style={{
                position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
                background: "transparent", border: "none", cursor: "pointer",
                color: "#64748b", fontSize: 16, padding: "4px 8px", lineHeight: 1,
              }}
            >
              ×
            </button>
          )}
        </div>

        {/* Filtri essenziali — singola riga compatta */}
        {(() => {
          const _yearFrom = (fromDate || "").slice(0, 4);
          const _yearTo = (toDate || "").slice(0, 4);
          const _isWholeYear =
            _yearFrom &&
            _yearFrom === _yearTo &&
            (fromDate || "").endsWith("-01-01") &&
            (toDate || "").endsWith("-12-31");
          const _selectedYear = Number(_yearFrom) || new Date().getFullYear();
          const _selectedMonth = _isWholeYear
            ? 0
            : Number((fromDate || "").slice(5, 7)) || (new Date().getMonth() + 1);
          return (
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
              <div style={{ minWidth: 110 }}>
                <label style={labelStyle}>Anno</label>
                <select
                  value={_selectedYear}
                  onChange={(e) => applyMonthYearQuickFilter(Number(e.target.value), _selectedMonth)}
                  style={inputStyle}
                  data-testid="pn-filter-quick-year"
                >
                  {(() => {
                    const cur = new Date().getFullYear();
                    const years = [];
                    for (let y = cur; y >= cur - 6; y--) years.push(y);
                    return years.map((y) => <option key={y} value={y}>{y}</option>);
                  })()}
                </select>
              </div>
              <div style={{ minWidth: 130 }}>
                <label style={labelStyle}>Mese</label>
                <select
                  value={_selectedMonth}
                  onChange={(e) => applyMonthYearQuickFilter(_selectedYear, Number(e.target.value))}
                  style={inputStyle}
                  data-testid="pn-filter-quick-month"
                >
                  <option value={0}>Tutti</option>
                  {["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"].map((label, i) => (
                    <option key={i + 1} value={i + 1}>{label}</option>
                  ))}
                </select>
              </div>
              <div style={{ minWidth: 110 }}>
                <label style={labelStyle}>Tipo</label>
                <select value={filterDirection} onChange={(e) => setFilterDirection(e.target.value)} style={inputStyle} data-testid="pn-filter-direction">
                  <option value="all">Tutti</option><option value="in">Entrate</option><option value="out">Uscite</option>
                </select>
              </div>
              <div style={{ minWidth: 150, position: "relative" }}>
                <label style={labelStyle}>Metodo</label>
                {(() => {
                  const summaryLabel =
                    filterMethods.length === 0
                      ? "Tutti"
                      : filterMethods.length === 1
                      ? filterMethods[0]
                      : `${filterMethods.length} metodi`;
                  const isActive = filterMethods.length > 0;
                  return (
                    <>
                      <button
                        type="button"
                        onClick={() => setMethodsMenuOpen((v) => !v)}
                        data-testid="pn-filter-method"
                        title={
                          filterMethods.length > 0
                            ? `Selezionati: ${filterMethods.join(", ")}`
                            : "Filtra per metodo di pagamento (multi-select)"
                        }
                        style={{
                          ...inputStyle,
                          textAlign: "left",
                          cursor: "pointer",
                          background: isActive ? "#eff6ff" : "white",
                          borderColor: isActive ? "#60a5fa" : "#cbd5e1",
                          fontWeight: isActive ? 600 : 400,
                          display: "flex", alignItems: "center", justifyContent: "space-between",
                        }}
                      >
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {summaryLabel}
                        </span>
                        <span style={{ marginLeft: 6, color: "#64748b", fontSize: 10 }}>
                          {methodsMenuOpen ? "▴" : "▾"}
                        </span>
                      </button>
                      {methodsMenuOpen && (
                        <>
                          {/* Backdrop click-outside */}
                          <div
                            onClick={() => setMethodsMenuOpen(false)}
                            style={{
                              position: "fixed", inset: 0, zIndex: 40, background: "transparent",
                            }}
                          />
                          <div
                            data-testid="pn-filter-method-menu"
                            style={{
                              position: "absolute", top: "100%", left: 0, marginTop: 4,
                              minWidth: "100%", zIndex: 50,
                              background: "white", border: "1px solid #cbd5e1",
                              borderRadius: 6, boxShadow: "0 4px 14px rgba(0,0,0,0.12)",
                              padding: 6,
                            }}
                          >
                            {METHODS.map((m) => {
                              const checked = filterMethods.includes(m);
                              return (
                                <label
                                  key={m}
                                  style={{
                                    display: "flex", alignItems: "center", gap: 8,
                                    padding: "5px 8px", cursor: "pointer",
                                    fontSize: 12, borderRadius: 4,
                                    background: checked ? "#eff6ff" : "transparent",
                                  }}
                                  onMouseEnter={(e) => (e.currentTarget.style.background = checked ? "#dbeafe" : "#f1f5f9")}
                                  onMouseLeave={(e) => (e.currentTarget.style.background = checked ? "#eff6ff" : "transparent")}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(e) =>
                                      setFilterMethods((curr) =>
                                        e.target.checked
                                          ? [...curr, m]
                                          : curr.filter((x) => x !== m)
                                      )
                                    }
                                    data-testid={`pn-filter-method-check-${m}`}
                                  />
                                  <span style={{ fontWeight: checked ? 600 : 400, color: checked ? "#1e40af" : "#334155" }}>{m}</span>
                                </label>
                              );
                            })}
                            {filterMethods.length > 0 && (
                              <div style={{ borderTop: "1px solid #e2e8f0", marginTop: 4, paddingTop: 6, display: "flex", justifyContent: "space-between" }}>
                                <button
                                  type="button"
                                  onClick={() => setFilterMethods([])}
                                  data-testid="pn-filter-method-clear"
                                  style={{ fontSize: 11, color: "#dc2626", background: "transparent", border: "none", cursor: "pointer", padding: "4px 8px" }}
                                >
                                  Deseleziona tutti
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setMethodsMenuOpen(false)}
                                  style={{ fontSize: 11, color: "#2563eb", background: "transparent", border: "none", cursor: "pointer", padding: "4px 8px", fontWeight: 600 }}
                                >
                                  Applica
                                </button>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </>
                  );
                })()}
              </div>
              <div style={{ minWidth: 130 }}>
                <label style={labelStyle}>Tipo attività</label>
                <select
                  value={filterManagementType}
                  onChange={(e) => setFilterManagementType(e.target.value)}
                  style={{
                    ...inputStyle,
                    borderColor: filterManagementType === "studio"
                      ? "#60a5fa"
                      : filterManagementType === "agenzia"
                      ? "#fb923c"
                      : "#cbd5e1",
                    background: filterManagementType === "studio"
                      ? "#eff6ff"
                      : filterManagementType === "agenzia"
                      ? "#fff7ed"
                      : "white",
                    fontWeight: 600,
                  }}
                  data-testid="pn-filter-management"
                >
                  <option value="agenzia">Agenzia</option>
                  <option value="studio">Studio</option>
                  <option value="all">Tutti</option>
                </select>
              </div>

              <button
                type="button"
                onClick={() => setShowAdvancedFilters((v) => !v)}
                data-testid="pn-toggle-advanced-filters"
                style={{
                  padding: "6px 12px",
                  background: showAdvancedFilters ? "#e0e7ff" : "white",
                  color: showAdvancedFilters ? "#3730a3" : "#475569",
                  border: `1px solid ${showAdvancedFilters ? "#a5b4fc" : "#cbd5e1"}`,
                  borderRadius: 4, cursor: "pointer", fontSize: 12, fontWeight: 600,
                  display: "inline-flex", alignItems: "center", gap: 6,
                }}
                title="Mostra/nascondi data Da-A, cliente, pratica, operatore, documento"
              >
                {showAdvancedFilters ? "▾" : "▸"} Filtri avanzati
                {advancedFiltersActiveCount > 0 && (
                  <span
                    data-testid="pn-advanced-filters-badge"
                    style={{
                      background: "#3730a3", color: "white",
                      borderRadius: 999, padding: "0 6px",
                      fontSize: 10, fontWeight: 700, minWidth: 16, textAlign: "center",
                    }}
                  >
                    {advancedFiltersActiveCount}
                  </span>
                )}
              </button>

              <button
                type="button"
                onClick={resetFilters}
                style={{
                  padding: "6px 10px", background: "white",
                  border: "1px solid #cbd5e1", borderRadius: 4,
                  cursor: "pointer", fontSize: 12, color: "#475569",
                }}
                data-testid="pn-reset-filters"
              >
                Reset filtri
              </button>
            </div>
          );
        })()}

        {/* Pannello "Filtri avanzati" — collassato di default */}
        {showAdvancedFilters && (
          <div
            data-testid="pn-advanced-filters-panel"
            style={{
              marginTop: 10, paddingTop: 10,
              borderTop: "1px dashed #cbd5e1",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: 8,
            }}
          >
            <div>
              <label style={labelStyle}>Da</label>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={inputStyle} data-testid="pn-filter-from" />
            </div>
            <div>
              <label style={labelStyle}>A</label>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={inputStyle} data-testid="pn-filter-to" />
            </div>
            <div>
              <label style={labelStyle}>Cliente</label>
              <select value={filterClientId} onChange={(e) => setFilterClientId(e.target.value)} style={inputStyle} data-testid="pn-filter-client">
                <option value="">Tutti</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.fullName || c.name}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Pratica</label>
              <select value={filterCaseId} onChange={(e) => setFilterCaseId(e.target.value)} style={inputStyle} data-testid="pn-filter-case">
                <option value="">Tutte</option>
                {cases.map((cc) => <option key={cc.id} value={cc.id}>{cc.caseNumber || cc.subject}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Operatore</label>
              <select value={filterOperatorId} onChange={(e) => setFilterOperatorId(e.target.value)} style={inputStyle} data-testid="pn-filter-operator">
                <option value="">Tutti</option>
                {operators.map((o) => <option key={o.id} value={o.id}>{o.fullName || o.name}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Documento</label>
              <select value={filterDocType} onChange={(e) => setFilterDocType(e.target.value)} style={inputStyle} data-testid="pn-filter-doctype">
                <option value="">Tutti</option><option value="fattura">Fattura</option><option value="proforma">Proforma</option><option value="none">Nessuno</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* TABLE */}
      <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 6, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }} data-testid="pn-table">
          <thead style={{ background: "#f1f5f9" }}>
            <tr>
              {[
                "Data", "Tipo", "Cliente", "Pratica", "Documento", "Descrizione",
                "Metodo", "Entrata", "Uscita", "Art. 15",
                ...(canSeeRunningBalance ? ["Saldo prog."] : []),
                "Operatore", ""
              ].map((h) => (
                <th key={h || "actions"} data-testid={h === "Saldo prog." ? "pn-col-saldo-prog" : undefined} style={{ padding: "8px 10px", textAlign: "left", color: "#475569", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4, borderBottom: "1px solid #e2e8f0" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={canSeeRunningBalance ? 13 : 12} style={{ padding: 20, textAlign: "center", color: "#94a3b8" }}>Caricamento…</td></tr>}
            {!loading && visibleRows.length === 0 && (
              <tr><td colSpan={canSeeRunningBalance ? 13 : 12} style={{ padding: 20, textAlign: "center", color: "#94a3b8" }}>
                {searchText ? `Nessun movimento contiene "${searchText}" nel periodo selezionato.` : "Nessun movimento per il periodo selezionato."}
              </td></tr>
            )}
            {!loading && visibleRows.map((m) => {
              const isIn = m.direction === "in";
              return (
                <tr key={m.id} style={{ borderBottom: "1px solid #f1f5f9" }} data-testid={`pn-row-${m.id}`}>
                  <td style={{ padding: "6px 10px", whiteSpace: "nowrap" }}>{fmtDateIT(m.date)}{m.time ? <span style={{ color: "#94a3b8" }}> {m.time}</span> : null}</td>
                  <td style={{ padding: "6px 10px" }}>
                    <span style={{ display: "inline-block", padding: "1px 8px", borderRadius: 999, fontSize: 11, fontWeight: 600, color: isIn ? "#065f46" : "#7f1d1d", background: isIn ? "#d1fae5" : "#fee2e2" }}>
                      {isIn ? "▲ Entrata" : "▼ Uscita"}
                    </span>
                  </td>
                  <td style={{ padding: "6px 10px" }}>{m.clientName || "—"}</td>
                  <td style={{ padding: "6px 10px" }}>{m.caseNumber || "—"}</td>
                  <td style={{ padding: "6px 10px" }}>
                    {m.invoiceNumber ? (
                      <>
                        <span style={{ fontWeight: 600 }}>{m.invoiceNumber}</span>
                        <span style={{ fontSize: 10, color: "#94a3b8", marginLeft: 4 }}>({m.invoiceDocType || "?"})</span>
                      </>
                    ) : "—"}
                  </td>
                  <td style={{ padding: "6px 10px", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }}>{m.description || "—"}</td>
                  <td style={{ padding: "6px 10px" }}>{m.paymentMethod}</td>
                  <td style={{ padding: "6px 10px", textAlign: "right", color: "#059669", fontWeight: 600 }}>{isIn ? eur(m.amount) : ""}</td>
                  <td style={{ padding: "6px 10px", textAlign: "right", color: "#dc2626", fontWeight: 600 }}>{!isIn ? eur(m.amount) : ""}</td>
                  <td style={{ padding: "6px 10px", textAlign: "right", color: "#7c3aed", fontWeight: 600 }} title="Art. 15 — Diritti, bolli e anticipazioni per conto cliente">{m.art15Amount ? eur(m.art15Amount) : ""}</td>
                  {canSeeRunningBalance && (
                    <td data-testid={`pn-saldo-prog-${m.id}`} style={{ padding: "6px 10px", textAlign: "right", fontWeight: 600, color: m._runningBalance >= 0 ? "#0f172a" : "#dc2626" }}>{eur(m._runningBalance)}</td>
                  )}
                  <td style={{ padding: "6px 10px", color: "#64748b" }}>{m.operatorName || "—"}</td>
                  <td style={{ padding: "6px 10px", textAlign: "right" }}>
                    {!m.readonly && (
                      <span style={{ display: "inline-flex", gap: 8 }}>
                        <button
                          type="button"
                          onClick={() => openCorrection(m)}
                          style={{ background: "none", border: "none", color: "#2563eb", cursor: "pointer", fontSize: 11, fontWeight: 600 }}
                          data-testid={`pn-correct-${m.id}`}
                          title="Modifica i dati del movimento (correzione tracciata)"
                        >
                          Correzione
                        </button>
                        <button
                          type="button"
                          onClick={() => cancelMovement(m)}
                          style={{ background: "none", border: "none", color: "#dc2626", cursor: "pointer", fontSize: 11 }}
                          data-testid={`pn-cancel-${m.id}`}
                          title="Storna il movimento mantenendo la tracciatura"
                        >
                          Storna
                        </button>
                      </span>
                    )}
                    {m.readonly && (
                      <span
                        title={m.invoiceNumber ? `Movimento legato a ${m.invoiceDocType || 'fattura'} ${m.invoiceNumber} — correggi dal documento` : "Movimento legato a un documento — gestire dal documento"}
                        style={{ fontSize: 11, color: "#94a3b8" }}
                      >
                        doc
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* DIALOG ENTRATA */}
      <MovementDialog
        open={showInDialog}
        direction="in"
        onClose={() => setShowInDialog(false)}
        onSaved={() => { setShowInDialog(false); refresh(); }}
        clients={clients} cases={cases} currentOperator={currentOperator}
        onOpenNewClient={onOpenNewClient}
        isClientDialogOpen={isClientDialogOpen}
        setClientForm={setClientForm}
        resetClientForm={resetClientForm}
        defaultManagementType={filterManagementType !== "all" ? filterManagementType : "agenzia"}
      />
      <MovementDialog
        open={showOutDialog}
        direction="out"
        onClose={() => setShowOutDialog(false)}
        onSaved={() => { setShowOutDialog(false); refresh(); }}
        clients={clients} cases={cases} currentOperator={currentOperator}
        onOpenNewClient={onOpenNewClient}
        isClientDialogOpen={isClientDialogOpen}
        setClientForm={setClientForm}
        resetClientForm={resetClientForm}
        defaultManagementType={filterManagementType !== "all" ? filterManagementType : "agenzia"}
      />
      {/* 11/06/2026 — Correzione movimento manuale. Stesso componente
          MovementDialog, in modalità "edit": precompila i campi e salva
          con PUT. Si apre quando l'utente clicca su "Correzione" su una
          riga manuale (non readonly). */}
      {editingMovement && (
        <MovementDialog
          open={true}
          direction={editingMovement.direction || "in"}
          editingMovement={editingMovement}
          onClose={() => setEditingMovement(null)}
          onSaved={() => { setEditingMovement(null); refresh(); }}
          clients={clients} cases={cases} currentOperator={currentOperator}
          onOpenNewClient={onOpenNewClient}
          isClientDialogOpen={isClientDialogOpen}
          setClientForm={setClientForm}
          resetClientForm={resetClientForm}
          defaultManagementType={filterManagementType !== "all" ? filterManagementType : "agenzia"}
        />
      )}
    </div>
  );
}

function MovementDialog({
  open, onClose, onSaved, direction, clients, cases, currentOperator,
  // P2: ClientCombobox inline-creation hooks
  onOpenNewClient, isClientDialogOpen, setClientForm, resetClientForm,
  // 11/06/2026 — Correzione movimento. Se passato `editingMovement`,
  // il dialog parte in modalità "edit" precompilato e salva con PUT;
  // altrimenti modalità "create" → POST.
  editingMovement = null,
  defaultManagementType = "agenzia",
}) {
  const isIn = direction === "in";
  const isEdit = !!editingMovement?.id;
  const [form, setForm] = useState(() => ({
    date: todayISO(), time: nowHM(),
    amount: "", paymentMethod: "Contanti",
    description: "", category: "",
    clientId: "", clientName: "", caseId: "", caseNumber: "",
    invoiceId: "", invoiceNumber: "", invoiceDocType: "",
    notes: "",
    art15Amount: "",
    managementType: defaultManagementType,
  }));
  const [saving, setSaving] = useState(false);
  const [invoices, setInvoices] = useState([]);
  // 11/06/2026 — Errore di validazione cliente (only for `in`).
  const [clientError, setClientError] = useState("");

  // P2: ricorda quale form era aperto quando l'utente clicca
  // "+ Crea nuovo cliente" → dopo il save del ClientDialog auto-selezioniamo
  // il nuovo cliente nel movimento. Stesso pattern di RubricaClientiTab.
  const [pendingMovementForm, setPendingMovementForm] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (editingMovement?.id) {
      // 11/06/2026 — Correzione: precompila col valore esistente.
      setForm({
        date: editingMovement.date || todayISO(),
        time: editingMovement.time || "",
        amount: String(editingMovement.amount ?? ""),
        paymentMethod: editingMovement.paymentMethod || "Contanti",
        description: editingMovement.description || "",
        category: editingMovement.category || (isIn ? "" : "Marca da bollo"),
        clientId: editingMovement.clientId || "",
        clientName: editingMovement.clientName || "",
        caseId: editingMovement.caseId || "",
        caseNumber: editingMovement.caseNumber || "",
        invoiceId: editingMovement.invoiceId || "",
        invoiceNumber: editingMovement.invoiceNumber || "",
        invoiceDocType: editingMovement.invoiceDocType || "",
        notes: editingMovement.notes || "",
        art15Amount: String(editingMovement.art15Amount ?? ""),
        managementType: editingMovement.managementType || defaultManagementType || "agenzia",
      });
    } else {
      setForm({
        date: todayISO(), time: nowHM(),
        amount: "", paymentMethod: "Contanti",
        description: "", category: isIn ? "" : "Marca da bollo",
        clientId: "", clientName: "", caseId: "", caseNumber: "",
        invoiceId: "", invoiceNumber: "", invoiceDocType: "",
        notes: "",
        art15Amount: "",
        managementType: defaultManagementType || "agenzia",
      });
    }
    setClientError("");
  }, [open, isIn, editingMovement, defaultManagementType]);

  // P2: handler per "+ Crea nuovo cliente" — apre il ClientDialog globale
  // con il nome pre-fillato, ricorda lo stato del form corrente per
  // auto-selezionare il cliente dopo il save. Il MovementDialog usa
  // `InlineModal` (non Radix Dialog) e resta visibile sotto il
  // ClientDialog globale (shadcn Dialog con portal) — alla chiusura del
  // ClientDialog il form viene aggiornato in place senza bisogno di
  // riaprire alcun modale.
  const handleCreateNewClient = useCallback((typedName) => {
    if (!onOpenNewClient) return;
    setPendingMovementForm({ ...form, _typedName: typedName });
    if (resetClientForm) resetClientForm();
    if (setClientForm) {
      setClientForm((prev) => ({ ...(prev || {}), fullName: typedName }));
    }
    onOpenNewClient();
  }, [form, onOpenNewClient, resetClientForm, setClientForm]);

  // P2: dopo la chiusura del ClientDialog globale, se eravamo nel
  // flusso "create from movement", trova il cliente appena creato per
  // nome e auto-selezionalo nel form già aperto.
  useEffect(() => {
    if (!pendingMovementForm) return;
    if (isClientDialogOpen) return; // attendi chiusura ClientDialog
    const typed = (pendingMovementForm._typedName || "").toLowerCase();
    const found = clients.find(
      (c) => ((c.fullName || c.name) || "").toLowerCase() === typed
    );
    setForm({
      ...pendingMovementForm,
      clientId: found?.id || pendingMovementForm.clientId || "",
      clientName: found?.fullName || found?.name || pendingMovementForm.clientName || "",
      caseId: "",
      caseNumber: "",
    });
    setPendingMovementForm(null);
  }, [isClientDialogOpen, pendingMovementForm, clients]);

  useEffect(() => {
    if (!isIn || !form.clientId) { setInvoices([]); return; }
    (async () => {
      try {
        const [inv, pro] = await Promise.all([
          api.request("/api/invoices"),
          api.request("/api/invoices?documentType=proforma"),
        ]);
        const all = [...(inv || []), ...(pro || [])].filter((x) => x.clientId === form.clientId);
        setInvoices(all);
      } catch { setInvoices([]); }
    })();
  }, [isIn, form.clientId]);

  const save = async () => {
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { toast.error("Importo non valido"); return; }
    if (!form.description.trim() && !form.category) { toast.error("Descrizione obbligatoria"); return; }
    // 11/06/2026 — Cliente obbligatorio in fase di registrazione INCASSO
    // (richiesta utente). Per le USCITE resta facoltativo (spese generiche).
    if (isIn && !form.clientId) {
      setClientError("Il cliente è obbligatorio per registrare un incasso.");
      toast.error("Seleziona un cliente per l'incasso");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        // 11/06/2026 — Correzione: PUT al record esistente. Il backend
        // tiene traccia dell'updatedAt; la cancellazione/storno resta
        // gestita dal pulsante "Storna" separato.
        const { invoiceId, invoiceNumber, invoiceDocType, ...editable } = form;
        await api.request(`/api/cash-movements/${editingMovement.id}`, {
          method: "PUT",
          body: JSON.stringify({
            ...editable,
            amount: amt,
            art15Amount: parseFloat(form.art15Amount) || 0,
          }),
        });
        toast.success("Movimento corretto");
      } else {
        await api.request("/api/cash-movements", {
          method: "POST",
          body: JSON.stringify({
            ...form,
            direction,
            amount: amt,
            // P4.b — Art. 15 inviato come float (anche se vuoto → 0).
            art15Amount: parseFloat(form.art15Amount) || 0,
          }),
        });
        toast.success(isIn ? "Incasso registrato" : "Uscita registrata");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.message || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  return (
    <InlineModal
      open={open}
      onClose={onClose}
      title={isEdit ? `Correzione movimento — ${isIn ? "Incasso" : "Uscita"}` : (isIn ? "Registra incasso" : "Registra uscita")}
      footer={
        <>
          <button type="button" onClick={onClose} style={{ padding: "6px 12px", background: "white", border: "1px solid #cbd5e1", borderRadius: 4, cursor: "pointer", fontSize: 13 }} data-testid="pn-modal-cancel-btn">Annulla</button>
          <button type="button" onClick={save} disabled={saving} style={{ padding: "6px 14px", background: isIn ? "#10b981" : "#ef4444", color: "white", border: "none", borderRadius: 4, cursor: saving ? "not-allowed" : "pointer", fontSize: 13, opacity: saving ? 0.6 : 1 }} data-testid="pn-modal-save-btn">{saving ? "Salvataggio…" : "Salva"}</button>
        </>
      }
    >
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div><label style={labelStyle}>Data *</label><input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} style={inputStyle} data-testid="pn-modal-date" /></div>
        <div><label style={labelStyle}>Ora</label><input type="time" value={form.time} onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))} style={inputStyle} data-testid="pn-modal-time" /></div>
        <div><label style={labelStyle}>Importo (€) *</label><input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} style={inputStyle} data-testid="pn-modal-amount" /></div>
        <div><label style={labelStyle}>Metodo pagamento *</label>
          <select value={form.paymentMethod} onChange={(e) => setForm((f) => ({ ...f, paymentMethod: e.target.value }))} style={inputStyle} data-testid="pn-modal-method">
            {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        {isIn && (
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={labelStyle}>Art. 15 — Diritti e Bolli (opzionale)</label>
            <input
              type="number" min="0" step="0.01"
              value={form.art15Amount}
              onChange={(e) => setForm((f) => ({ ...f, art15Amount: e.target.value }))}
              placeholder="0,00"
              style={inputStyle}
              data-testid="pn-modal-art15"
            />
            <div style={{ fontSize: 11, color: "#7c3aed", marginTop: 4 }}>
              Importo anticipato per conto cliente (diritti, bolli, visure, ecc.).
              Non altera l&apos;importo dell&apos;incasso, è solo per il monitoraggio Art. 15.
            </div>
          </div>
        )}
        {!isIn && (
          <div style={{ gridColumn: "1 / -1" }}><label style={labelStyle}>Categoria spesa</label>
            <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} style={inputStyle} data-testid="pn-modal-category">
              {OUT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        )}
        <div style={{ gridColumn: "1 / -1" }}><label style={labelStyle}>Descrizione</label>
          <input type="text" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder={isIn ? "Es. acconto cliente, recupero spese" : "Es. marca da bollo 16€"} style={inputStyle} data-testid="pn-modal-description" />
        </div>
        <div><label style={labelStyle}>Cliente {isIn ? "*" : "(opzionale)"}</label>
          <ClientCombobox
            value={form.clientId}
            onChange={(clientId, clientFullName) => {
              setForm((f) => ({ ...f, clientId, clientName: clientFullName || "", caseId: "", caseNumber: "" }));
              if (clientId) setClientError("");
            }}
            clients={clients.map((c) => ({
              id: c.id,
              fullName: c.fullName || c.name || "",
              clientType: c.clientType || "",
            }))}
            onCreateNew={onOpenNewClient ? handleCreateNewClient : undefined}
            placeholder="Digita per cercare cliente..."
            testId="pn-modal-client"
          />
          {clientError && (
            <div data-testid="pn-modal-client-error" style={{ marginTop: 4, color: "#dc2626", fontSize: 11, fontWeight: 600 }}>
              {clientError}
            </div>
          )}
        </div>
        <div><label style={labelStyle}>Pratica (opzionale)</label>
          <select value={form.caseId} onChange={(e) => {
            const cc = cases.find((x) => x.id === e.target.value);
            setForm((f) => ({ ...f, caseId: e.target.value, caseNumber: cc?.caseNumber || "" }));
          }} style={inputStyle} data-testid="pn-modal-case">
            <option value="">—</option>
            {cases.filter((cc) => !form.clientId || cc.clientId === form.clientId).map((cc) => <option key={cc.id} value={cc.id}>{cc.caseNumber || cc.subject}</option>)}
          </select>
        </div>
        {isIn && form.clientId && invoices.length > 0 && (
          <div style={{ gridColumn: "1 / -1" }}><label style={labelStyle}>Documento collegato (Strategia A: aggiorna direttamente il documento)</label>
            <select value={form.invoiceId} onChange={(e) => {
              const inv = invoices.find((x) => x.id === e.target.value);
              setForm((f) => ({
                ...f,
                invoiceId: e.target.value,
                invoiceNumber: inv?.invoiceNumber || "",
                invoiceDocType: inv?.documentType === "proforma" ? "proforma" : "fattura",
              }));
            }} style={inputStyle} data-testid="pn-modal-invoice">
              <option value="">— Incasso libero (no documento) —</option>
              {invoices.map((inv) => (
                <option key={inv.id} value={inv.id}>
                  {inv.documentType === "proforma" ? "Proforma" : "Fattura"} {inv.invoiceNumber} — {eur(inv.totale)}
                </option>
              ))}
            </select>
          </div>
        )}
        <div style={{ gridColumn: "1 / -1" }}><label style={labelStyle}>Tipo attività *</label>
          <select
            value={form.managementType}
            onChange={(e) => setForm((f) => ({ ...f, managementType: e.target.value }))}
            style={{
              ...inputStyle,
              borderColor: form.managementType === "studio" ? "#60a5fa" : "#fb923c",
              background: form.managementType === "studio" ? "#eff6ff" : "#fff7ed",
              fontWeight: 600,
            }}
            data-testid="pn-modal-management-type"
          >
            <option value="agenzia">Agenzia</option>
            <option value="studio">Studio</option>
          </select>
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>
            Determina in quale area gestionale viene contabilizzato il movimento.
          </div>
        </div>
        <div style={{ gridColumn: "1 / -1" }}><label style={labelStyle}>Note</label>
          <textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} style={{ ...inputStyle, minHeight: 60, fontFamily: "inherit" }} data-testid="pn-modal-notes" />
        </div>
        <div style={{ gridColumn: "1 / -1", fontSize: 11, color: "#64748b", padding: "6px 8px", background: "#f8fafc", borderLeft: "3px solid #cbd5e1", borderRadius: 4 }}>
          <strong>Operatore:</strong> {currentOperator?.fullName || "—"} (tracciato automaticamente)
        </div>
      </div>
    </InlineModal>
  );
}
